import { tiempoRelativo } from './helpers';

// Columna JSON de destinatarios -> [{ email, nombre }]. Tolera string JSON,
// null y basura (-> []), y descarta entradas sin email.
const listaDirecciones = (valor) => {
  let lista = valor;
  if (typeof lista === 'string') {
    try {
      lista = JSON.parse(lista);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(lista)) return [];
  return lista
    .filter((d) => d && d.email)
    .map((d) => ({ email: String(d.email), nombre: d.nombre || null }));
};

// Convierte un comentario del backend al modelo que consume TicketThread:
//   { tipo: 'req' | 'agente' | 'nota', autor, tiempo, texto }
// - nota interna  -> es_publico === false
// - respuesta del agente -> autor_tipo === 'agente'
// - mensaje del solicitante -> resto
export const comentarioAMensaje = (c) => {
  let tipo = 'req';
  if (c.es_publico === false) tipo = 'nota';
  else if (c.autor_tipo === 'agente') tipo = 'agente';

  const autor =
    c.autor_agente_nombre || c.autor_contacto_nombre || c.autor_contacto_email || 'Sistema';

  return {
    id: c.id,
    tipo,
    autor,
    tiempo: tiempoRelativo(c.creado_en),
    texto: c.cuerpo,
    // Solo comentarios entrados por email desde el despliegue de cuerpo_html;
    // agentes e históricos llevan null y caen al texto plano (fallback).
    textoHtml: c.cuerpo_html || null,
    esPublico: c.es_publico !== false,
    emailMessageId: c.email_message_id || null,
    // Destinatarios del correo ([{ email, nombre }]): el entrante tal como llego
    // y la respuesta del agente tal como salio. [] en comentarios antiguos.
    para: listaDirecciones(c.email_para),
    cc: listaDirecciones(c.email_cc),
  };
};

// Normaliza el objeto de filtros de la UI a query params del endpoint,
// descartando vacios/null para no enviar parametros inertes. El front usa
// `estado`/`agente` como ids, pero el filtro del backend los espera como
// `estado_id`/`agente_id` (igual que el body del PATCH); sin este mapeo las
// vistas "Esperando"/"Cerradas" (estado) y "Mis tickets" (agente) enviaban un
// parametro que el backend ignora y no filtraban nada. El resto ya coincide.
const FILTRO_A_PARAM = { estado: 'estado_id', agente: 'agente_id' };
export const filtrosAParams = (filtros = {}) => {
  const params = {};
  const keys = ['estado', 'grupo_id', 'agente', 'tema_id', 'prioridad', 'search'];
  keys.forEach((k) => {
    let v = filtros[k];
    // Multi-selección (p. ej. estados): array -> CSV, que el backend acepta
    // como multi-valor (?estado_id=1,4 -> IN). Array vacío = sin filtro.
    if (Array.isArray(v)) v = v.length ? v.join(',') : undefined;
    if (v !== undefined && v !== null && v !== '') params[FILTRO_A_PARAM[k] ?? k] = v;
  });
  if (filtros.sin_asignar) params.sin_asignar = true;
  return params;
};

// El backend devuelve, por cada relacion del ticket, el id y el nombre resuelto
// por separado: `estado_id` + `estado`(descripcion) y `agente_id` + `agente`(nombre).
// El resto del front trata `estado`/`agente` como ids: los <select> de propiedades,
// EstadoBadge (mapea id -> meta), slaEstado (Number(estado)) y nombreUsuario
// (resuelve id -> nombre desde el catalogo de usuarios). Sin esta normalizacion,
// un ticket asignado se mostraba "Sin asignar", el badge de estado salia "—" y el
// SLA se calculaba sobre NaN. El `??` deja pasar datos que ya traigan el id en
// `estado`/`agente` (mocks de test, seeds antiguos). No tocamos `grupo`/`tema`
// (nombres) porque la cola los pinta tal cual y las props leen `grupo_id`/`tema_id`.
export const normalizarTicket = (t) =>
  t && typeof t === 'object'
    ? { ...t, estado: t.estado_id ?? t.estado, agente: t.agente_id ?? t.agente }
    : t;
